import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { OptionKey } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { api } from '@/api';
import { ALL_KEY, RAW_DATE_FORMAT } from '@/common/constants';
import { MONEY_TRANSFER } from '@/common/paths';
import { Pagination } from '@/common/types';
import {
  MoneyTransferPayload,
  MoneyTransferStatus,
  MoneyTransferType,
} from '@/modules/investments/investments.types';

export const QUERY_KEY = 'GET_INITIATED_TRANSACTIONS_HISTORY';

interface Response {
  id: number;
  amount: number;
  created_at: string;
  type: MoneyTransferType;
  status: MoneyTransferStatus;
}

interface Params {
  statuses: OptionKey[];
  isStandAloneMode?: boolean;
  perPage?: number;
  startDate: Date | null;
  endDate: Date | null;
}
const PER_PAGE = 1000;
const PENDING_STATUSES = [
  MoneyTransferStatus.CreatedInternal,
  MoneyTransferStatus.CreatedExternal,
  MoneyTransferStatus.SuccessfulExternal,
];
const useInitiatedTransactionsQuery = (params?: Params) => {
  const appliedStatuses = useMemo(() => {
    const list = params?.statuses;
    if (params?.isStandAloneMode && list) {
      return list.join(',');
    }
    if (list?.includes(ALL_KEY)) {
      return null;
    }
    if (!list || !list.length || list.length === 2) {
      return null;
    }
    if (list.includes(MoneyTransferStatus.CreatedInternal)) {
      return PENDING_STATUSES.join(',');
    }
    return list.join(',');
  }, [params?.statuses, params?.isStandAloneMode]);
  const { isLoading, isSuccess, data } = useQuery(
    [QUERY_KEY, params?.startDate, params?.endDate, params?.perPage, ...(params?.statuses || [])],
    () =>
      // TODO: types not exists
      api.get<Pagination<Response>>(MONEY_TRANSFER, {
        params: _.pickBy({
          // sort: 'createdAt,desc',
          start_date: params?.startDate ? dayjs(params?.startDate).format(RAW_DATE_FORMAT) : null,
          end_date: params?.endDate ? dayjs(params?.endDate).format(RAW_DATE_FORMAT) : null,
          statuses: appliedStatuses,
          size: params?.perPage || PER_PAGE,
        }),
      })
  );

  const formattedData = useMemo(() => {
    const list = data?.data?.content || [];
    return list.map((item) => {
      const formattedItem: MoneyTransferPayload = {
        id: `${item.id}`,
        type: item.type,
        amount: item.amount,
        date: dayjs(item.created_at),
        status: item.status,
      };
      return formattedItem;
    });
  }, [data?.data?.content]);
  const total = useMemo(() => data?.data?.totalElements || 0, [data?.data?.totalElements]);
  const numberOfElements = useMemo(() => data?.data?.numberOfElements || 0, [data?.data?.numberOfElements]);
  const isEmpty = useMemo(() => total === 0 && isSuccess, [isSuccess, total]);
  return {
    isLoading,
    isEmpty,
    total,
    isSuccess,
    numberOfElements,
    data: formattedData,
  };
};

export default useInitiatedTransactionsQuery;
